import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(() => Promise.resolve(false));

// Confirmation in-app (remplace window.confirm) — fonctionne sur tous les appareils,
// y compris en mode PWA/standalone sur tablette où window.confirm est ignoré.
export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const resolver = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts(typeof options === "string" ? { description: options } : options);
    setOpen(true);
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const settle = (value) => {
    setOpen(false);
    if (resolver.current) { resolver.current(value); resolver.current = null; }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) settle(false); }}>
        <AlertDialogContent className="rounded-2xl max-w-[92vw] sm:max-w-lg" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{opts.title || "Confirmer l'action"}</AlertDialogTitle>
            {opts.description && <AlertDialogDescription className="leading-relaxed">{opts.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)} data-testid="confirm-cancel" className="rounded-xl h-11">
              {opts.cancelText || "Annuler"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              data-testid="confirm-accept"
              className={`rounded-xl h-11 ${opts.destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}`}
            >
              {opts.confirmText || "Confirmer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
